/**
 * Google Sheets Leaderboard API
 *
 * Both reads and writes go directly to Google Apps Script.
 * Writes include an API key for authentication.
 */

import { GOOGLE_SCRIPT_URL, GOOGLE_SCRIPT_READ_URL, BEANWARS_API_KEY, BLACKBIRD_CAFE_URL as CAFE_URL } from '../config.js';

export const BLACKBIRD_CAFE_URL = CAFE_URL;

export const isGoogleSheetsConfigured = () => {
  return !!GOOGLE_SCRIPT_READ_URL;
};

/**
 * Save a score directly to Google Apps Script (with API key)
 */
export async function saveScore(scoreData) {
  try {
    if (!scoreData.playerName || typeof scoreData.playerName !== 'string') {
      return { success: false, error: new Error('Player name is required') };
    }
    if (typeof scoreData.score !== 'number' || !Number.isFinite(scoreData.score)) {
      return { success: false, error: new Error('Invalid score') };
    }
    if (!['cloak', 'migo'].includes(scoreData.side)) {
      return { success: false, error: new Error('Invalid side') };
    }

    if (!GOOGLE_SCRIPT_URL) {
      return { success: false, error: new Error('Leaderboard not configured') };
    }

    const payload = {
      action: 'addScore',
      playerName: scoreData.playerName,
      playerEmail: scoreData.playerEmail || '',
      playerWallet: scoreData.playerWallet || '',
      characterId: scoreData.characterId,
      characterName: scoreData.characterName,
      characterImage: scoreData.characterImage,
      side: scoreData.side,
      score: scoreData.score,
      levelReached: scoreData.levelReached,
      runTime: scoreData.runTime,
      specialsUsed: scoreData.specialsUsed,
      heartsRemaining: scoreData.heartsRemaining
    };

    const url = `${GOOGLE_SCRIPT_URL}?key=${encodeURIComponent(BEANWARS_API_KEY)}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    const data = await response.json();

    if (data.error) {
      console.error('Score submission failed:', data.error);
      return { success: false, error: new Error(data.error) };
    }

    console.log('Score submitted to Google Apps Script');
    return { success: true };
  } catch (error) {
    console.error('Error saving score:', error);
    return { success: false, error };
  }
}

/**
 * Get leaderboard scores directly from Apps Script (public, no auth)
 */
export async function getLeaderboard(side = 'all', limit = 500, verified = false) {
  if (!GOOGLE_SCRIPT_READ_URL) {
    console.error('GOOGLE_SCRIPT_READ_URL not configured');
    return { success: false, error: new Error('Leaderboard not configured'), data: [] };
  }

  try {
    let url = `${GOOGLE_SCRIPT_READ_URL}?action=getScores&side=${encodeURIComponent(side)}&limit=${Math.min(limit, 500)}`;
    if (verified) {
      url += '&verified=true';
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000);

    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (data.error) {
      throw new Error(data.error);
    }

    const normalizedData = (data.scores || []).map(score => {
      const entry = {
        player_name: score.playerName,
        has_email: score.hasEmail || false,
        has_wallet: score.hasWallet || false,
        prize_eligible: score.hasEmail || score.hasWallet || false,
        character_id: score.characterId,
        character_name: score.characterName,
        character_image: score.characterImage,
        side: score.side,
        score: score.score,
        level_reached: score.levelReached,
        run_time: score.runTime,
        specials_used: score.specialsUsed,
        hearts_remaining: score.heartsRemaining,
        created_at: score.timestamp
      };

      if (typeof entry.score === 'string' && ['cloak', 'migo'].includes(entry.score)) {
        console.warn('[Leaderboard] Column shift detected — remapping fields');
        const shifted = {
          character_id: score.characterName || '',
          character_name: score.characterImage || '',
          character_image: score.side || '',
          side: score.score || '',
          score: Number(score.levelReached) || 0,
          level_reached: Number(score.runTime) || 0,
          run_time: Number(score.specialsUsed) || 0,
          specials_used: Number(score.heartsRemaining) || 0,
          hearts_remaining: 0
        };
        Object.assign(entry, shifted);
      }

      return entry;
    });

    console.log('Fetched leaderboard:', normalizedData.length, 'entries', verified ? '(verified only)' : '');
    return { success: true, data: normalizedData };
  } catch (error) {
    console.error('Error fetching leaderboard:', error);
    return { success: false, error, data: [] };
  }
}
